import { useNavigate } from "@solidjs/router";
import {
  ensureWebSocketURL,
  getTagValue,
  KnownEvent,
} from "applesauce-core/helpers";
import { MailboxesFactory } from "applesauce-core/factories";
import { kinds } from "nostr-tools";
import { map } from "rxjs";
import { createSignal, For, from, onMount } from "solid-js";

import { BackIcon, RemoveIcon } from "../components/icons";
import { LOCAL_MESH_RELAY } from "../const";
import { toastOperation } from "../helpers/toast";
import { accounts } from "../services/accounts";
import { activeMailboxes } from "../services/lifestyle";
import { pool, publish } from "../services/pool";
import {
  defaultRelays,
  localMeshRelayEnabled,
  lookupRelays,
} from "../services/settings";
import { eventStore } from "../services/stores";

function RelayRow(props: { url: string; onRemove?: () => void }) {
  return (
    <div class="flex items-center justify-between gap-2 border-b border-base-300 p-2 last:border-b-0">
      <span class="truncate">{props.url}</span>
      <button
        onClick={props.onRemove}
        class="btn btn-ghost btn-error btn-square btn-sm"
        aria-label="remove relay"
      >
        <RemoveIcon />
      </button>
    </div>
  );
}

function AddRelayForm(props: {
  onSubmit?: (url: string) => void | Promise<void>;
}) {
  const [submitting, setSubmitting] = createSignal(false);
  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const url = formData.get("url")?.toString().trim();

    if (url && props.onSubmit) {
      setSubmitting(true);
      await props.onSubmit(ensureWebSocketURL(url));
      (e.target as HTMLFormElement).reset();
      setSubmitting(false);
    }
  };

  // load online relays
  onMount(() =>
    pool
      .subscription(["wss://relay.nostr.watch"], { kinds: [30166], limit: 400 })
      .subscribe((event) => eventStore.add(event)),
  );

  // Subscribe to an array of relays urls from the 30166 events
  const relays = from(
    eventStore
      .timeline({ kinds: [30166] })
      .pipe(
        map(
          (events) =>
            events
              ?.map((e) => getTagValue(e, "d"))
              .filter((url) => !!url) as string[],
        ),
      ),
  );

  return (
    <form onSubmit={handleSubmit} class="mt-4 space-y-2">
      <input
        list="relays"
        type="text"
        name="url"
        placeholder="Enter relay URL or hostname..."
        required
        class="input input-bordered w-full"
        disabled={submitting()}
      />
      <datalist id="relays">
        <For each={relays() ?? []}>
          {(url) => <option value={url}>{url}</option>}
        </For>
      </datalist>
      <button
        type="submit"
        class="btn btn-primary w-full"
        disabled={submitting()}
      >
        {submitting() ? "Saving..." : "Add Relay"}
      </button>
    </form>
  );
}

function MailboxSettings() {
  const account = from(accounts.active$);
  const relays = from(activeMailboxes);

  const addRelay = toastOperation(
    async (url: string) => {
      const mailboxes = eventStore.getReplaceable(
        kinds.RelayList,
        account()!.pubkey,
      );
      if (!mailboxes) return;

      const signed = await MailboxesFactory.modify(
        mailboxes as KnownEvent<typeof kinds.RelayList>,
      )
        .addOutbox(url)
        .sign(accounts.signer);
      await publish(signed);
    },
    {
      loading: "Adding relay...",
      success: "Relay added",
      error: "Failed to add relay",
    },
  );
  const removeRelay = toastOperation(
    async (url: string) => {
      const mailboxes = eventStore.getReplaceable(
        kinds.RelayList,
        account()!.pubkey,
      );
      if (!mailboxes) return;

      const signed = await MailboxesFactory.modify(
        mailboxes as KnownEvent<typeof kinds.RelayList>,
      )
        .removeOutbox(url)
        .sign(accounts.signer);
      await publish(signed);
    },
    {
      loading: "Removing relay...",
      success: "Relay removed",
      error: "Failed to remove relay",
    },
  );

  if (account() === undefined) return null;

  return (
    <div class="mx-auto max-w-md">
      <h1 class="mb-4 text-2xl font-bold">Data Relays</h1>

      <div class="card bg-base-100 shadow">
        <For each={relays()?.outboxes}>
          {(relay) => (
            <RelayRow url={relay} onRemove={() => removeRelay.run(relay)} />
          )}
        </For>
      </div>

      <AddRelayForm onSubmit={addRelay.run} />
    </div>
  );
}

function DefaultRelaysSettings() {
  const relays = from(defaultRelays);

  const addRelay = (url: string) => {
    const current = defaultRelays.getValue();
    if (!current.includes(url)) {
      defaultRelays.next([...current, url]);
    }
  };

  const removeRelay = (url: string) => {
    const current = defaultRelays.getValue();
    defaultRelays.next(current.filter((relay) => relay !== url));
  };

  return (
    <div class="mx-auto max-w-md">
      <h1 class="mb-4 text-2xl font-bold">Data Relays</h1>

      <div class="card bg-base-100 shadow">
        <For each={relays()}>
          {(relay) => (
            <RelayRow url={relay} onRemove={() => removeRelay(relay)} />
          )}
        </For>
      </div>

      <AddRelayForm onSubmit={addRelay} />
    </div>
  );
}

function LookupSettings() {
  const relays = from(lookupRelays);
  const addLookupRelay = async (url: string) => {
    const current = lookupRelays.getValue();
    if (!current.includes(url)) {
      lookupRelays.next([...current, url]);
    }
  };
  const removeLookupRelay = (url: string) => {
    const current = lookupRelays.getValue();
    lookupRelays.next(current.filter((relay) => relay !== url));
  };

  return (
    <div class="mx-auto max-w-md">
      <h1 class="mb-4 text-2xl font-bold">Lookup Relays</h1>

      <div class="card bg-base-100 shadow">
        <For each={relays() ?? []}>
          {(relay) => (
            <RelayRow url={relay} onRemove={() => removeLookupRelay(relay)} />
          )}
        </For>
      </div>

      <AddRelayForm onSubmit={addLookupRelay} />
    </div>
  );
}

function LocalMeshRelaySettings() {
  const enabled = from(localMeshRelayEnabled);

  return (
    <div class="mx-auto mb-6 max-w-md">
      <h1 class="mb-4 text-2xl font-bold">Experimental Mesh</h1>

      <label class="card bg-base-100 shadow cursor-pointer">
        <div class="card-body flex-row items-start gap-3 p-4">
          <input
            type="checkbox"
            class="checkbox checkbox-primary mt-1"
            checked={enabled()}
            onChange={(event) =>
              localMeshRelayEnabled.next(event.currentTarget.checked)
            }
          />
          <span>
            <span class="block font-semibold">Use local peer relay</span>
            <span class="block text-sm text-base-content/70">
              Also read and publish events through {LOCAL_MESH_RELAY} when a
              local mesh relay is running.
            </span>
          </span>
        </div>
      </label>
    </div>
  );
}

function SettingsView() {
  const navigate = useNavigate();
  const account = from(accounts.active$);

  return (
    <>
      <div class="flex-grow overflow-auto bg-base-200 p-4">
        <LocalMeshRelaySettings />
        <LookupSettings />
        {account() ? <MailboxSettings /> : <DefaultRelaysSettings />}
      </div>

      <footer class="bg-primary text-primary-content p-2 pb-safe-or-2 flex items-center">
        <button
          onClick={() => navigate(-1)}
          class="btn btn-ghost btn-square"
          aria-label="back"
        >
          <BackIcon />
        </button>
      </footer>
    </>
  );
}

export default SettingsView;
